import { useEffect, useState } from "react";
import aiBotInactiveIcon from "@src/assets/svg/ai-bot-inactive-icon.svg";
import aiBotActiveIcon from "@src/assets/svg/ai-bot-red-icon.svg";
import distributorIcon from "@src/assets/svg/distributor-icon.svg";
import distributorInactiveIcon from "@src/assets/svg/distrubutor-inactive-icon.svg";
import factoryIcon from "@src/assets/svg/factory-icon.svg";
import factoryInactiveIcon from "@src/assets/svg/factorry-inactive-icon.svg";
import oemIcon from "@src/assets/svg/oem-icon.svg";
import oemInactiveIcon from "@src/assets/svg/oem-inactive-icon.svg";
import supplierIcon from "@src/assets/svg/sup-icon.svg";
import supplierInactiveIcon from "@src/assets/svg/sup-icon-inactive.svg";
import { useI18N } from "@src/language/language-context";
import { getOrganizationByIdUseCase } from "@src/cloud-connect/namespace-configuration/use-cases/get-organization-by-id-use-case";
import { createRoutingPathUseCase } from "@src/cloud-connect/namespace-configuration/use-cases/create-routing-path-use-case";
import { updateRoutingPathUseCase } from "@src/cloud-connect/namespace-configuration/use-cases/update-routing-path-use-case";
import {
  errorSnackbar,
  successSnackbar,
  warningSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import {
  CrossNameSpaceOrganziation,
  MachineGroup,
  OrganizationType,
  UpdateRoutingPathPayload,
} from "@src/types/namespace-configuration";

/**
 * Routing metadata and business logic for MachineGroupExpandedView. The hook
 * loads the owning organization, hydrates and edits stages, persists changes,
 * and returns a render-ready view model for the paired component.
 */
// This order is the routing path order and defines which stages are before or
// after the owning organization's fixed starting stage.
const ticketRoutingStages = [
  {
    type: "factory",
    activeIcon: factoryIcon,
    inactiveIcon: factoryInactiveIcon,
  },
  {
    type: "ai_bot",
    activeIcon: aiBotActiveIcon,
    inactiveIcon: aiBotInactiveIcon,
  },
  {
    type: "distributor",
    activeIcon: distributorIcon,
    inactiveIcon: distributorInactiveIcon,
  },
  { type: "OEM", activeIcon: oemIcon, inactiveIcon: oemInactiveIcon },
  {
    type: "support",
    activeIcon: supplierIcon,
    inactiveIcon: supplierInactiveIcon,
  },
] as const;

type TicketRoutingStageType = Exclude<OrganizationType, "other"> | "ai_bot";

// Organizations can be selected for these stages; BOT remains a fixed field
// between factory and distributor rather than a selectable organization.
const ticketRoutingDropdowns = [
  { type: "factory", placeholderKey: "addFactory" },
  { type: "distributor", placeholderKey: "addDistributor" },
  { type: "OEM", placeholderKey: "addOem" },
  { type: "support", placeholderKey: "addSupplier" },
] as const;

type RoutingDropdownType = (typeof ticketRoutingDropdowns)[number]["type"];

// Short labels shown in the routing timeline, separate from internal type names.
const ticketRoutingStageCodes: Record<TicketRoutingStageType, string> = {
  factory: "FAC",
  ai_bot: "BOT",
  distributor: "DIS",
  OEM: "OEM",
  support: "SUP",
};

// Serialize active stages in their configured order so snapshots compare
// consistently, regardless of the order in which the user toggled them.
type RoutingSelections = Record<RoutingDropdownType, string>;

const createEmptyRoutingSelections = (): RoutingSelections => ({
  factory: "",
  distributor: "",
  OEM: "",
  support: "",
});

const getRoutingStateKey = (
  activeStages: TicketRoutingStageType[],
  selections: RoutingSelections,
) =>
  JSON.stringify({
    activeStages: ticketRoutingStages
      .filter((stage) => activeStages.includes(stage.type))
      .map((stage) => stage.type),
    selections: ticketRoutingDropdowns.map(({ type }) => [
      type,
      selections[type] || null,
    ]),
  });

interface UseMachineGroupExpandedViewHookProps {
  machineGroup: MachineGroup;
  onRoutingPathSaved: () => void;
  organizations: CrossNameSpaceOrganziation[];
  permissions: string[];
}

/**
 * Loads and manages the routing path for one machine group, then exposes the
 * current state and callbacks needed by MachineGroupExpandedView.
 */
export const useMachineGroupExpandedViewHook = ({
  machineGroup,
  onRoutingPathSaved,
  organizations,
  permissions,
}: UseMachineGroupExpandedViewHookProps) => {
  const i18n = useI18N();
  const texts = {
    noMachinesAdded: i18n("modules.machineGroupExpandedView.noMachinesAdded"),
    ticketRoutingPath: i18n(
      "modules.machineGroupExpandedView.ticketRoutingPath",
    ),
    addBot: i18n("modules.machineGroupExpandedView.addBot"),
    addFactory: i18n("modules.machineGroupExpandedView.addFactory"),
    addDistributor: i18n("modules.machineGroupExpandedView.addDistributor"),
    addOem: i18n("modules.machineGroupExpandedView.addOem"),
    addSupplier: i18n("modules.machineGroupExpandedView.addSupplier"),
    saveRoutingPath: i18n("modules.machineGroupExpandedView.saveRoutingPath"),
    errorCreatingRoutingPath: i18n(
      "modules.machineGroupExpandedView.errorCreatingRoutingPath",
    ),
    errorUpdatingRoutingPath: i18n(
      "modules.machineGroupExpandedView.errorUpdatingRoutingPath",
    ),
    routingPathCreatedSuccessfully: i18n(
      "modules.machineGroupExpandedView.routingPathCreatedSuccessfully",
    ),
    routingPathUpdatedSuccessfully: i18n(
      "modules.machineGroupExpandedView.routingPathUpdatedSuccessfully",
    ),
    routingStageEnableTooltip: i18n(
      "modules.machineGroupExpandedView.routingStageEnableTooltip",
    ),
    routingStageBeforeStartTooltip: i18n(
      "modules.machineGroupExpandedView.routingStageBeforeStartTooltip",
    ),
    routingPathUnchangedTooltip: i18n(
      "modules.machineGroupExpandedView.routingPathUnchangedTooltip",
    ),
    failedToFetchOrganizations: i18n(
      "modules.machineGroupExpandedView.failedToFetchOrganizations",
    ),
    selectOrganizationsForActiveRoutingStages: i18n(
      "modules.machineGroupExpandedView.selectOrganizationsForActiveRoutingStages",
    ),
    activateRoutingStage: i18n(
      "modules.machineGroupExpandedView.activateRoutingStage",
    ),
    deactivateRoutingStage: i18n(
      "modules.machineGroupExpandedView.deactivateRoutingStage",
    ),
    factoryRoutingStageAlwaysEnabled: i18n(
      "modules.machineGroupExpandedView.factoryRoutingStageAlwaysEnabled",
    ),
    routingStageFixedForOrganization: i18n(
      "modules.machineGroupExpandedView.routingStageFixedForOrganization",
    ),
    botOrganizationName: i18n(
      "modules.machineGroupExpandedView.botOrganizationName",
    ),
    routingStage: i18n("modules.machineGroupExpandedView.routingStage"),
    loadingTicketRoutingPath: i18n(
      "modules.machineGroupExpandedView.loadingTicketRoutingPath",
    ),
  };
  // Viewing and editing are separate capabilities: a user may see the path
  // without being allowed to change its stages or save it.
  const canViewRelationships = permissions.includes(
    "view-namespace-relationships",
  );
  const canManageRelationships = permissions.includes(
    "manage-namespace-relationships",
  );
  // The owning organization determines the immutable first stage in the path.
  const [owningOrganization, setOwningOrganization] =
    useState<CrossNameSpaceOrganziation | null>(null);
  const [isLoadingOwningOrganization, setIsLoadingOwningOrganization] =
    useState(true);
  // One hook instance belongs to one expanded group, so organization choices
  // only need to be keyed by their routing stage, not a composite string.
  const [routingSelections, setRoutingSelections] = useState<RoutingSelections>(
    createEmptyRoutingSelections,
  );
  const [isSavingRoutingPath, setIsSavingRoutingPath] = useState(false);
  // Compare this snapshot with current selections to detect unsaved changes.
  const [savedRoutingStateKey, setSavedRoutingStateKey] = useState<
    string | null
  >(null);
  // There are only five fixed stages; an array keeps membership and toggles
  // straightforward without cloning or mutating Set instances.
  const [activeRoutingStages, setActiveRoutingStages] = useState<
    TicketRoutingStageType[]
  >([]);
  // An `other` organization type cannot anchor a known routing stage.
  const owningOrganizationStage: TicketRoutingStageType | null =
    owningOrganization && owningOrganization.type !== "other"
      ? owningOrganization.type
      : null;
  // Stage indices let the hook consistently determine which stages are
  // locked before the owner, fixed at the owner, or editable after it.
  const owningOrganizationStageIndex = owningOrganizationStage
    ? ticketRoutingStages.findIndex(
        (stage) => stage.type === owningOrganizationStage,
      )
    : -1;
  const isStageAfterOwningOrganization = (stageType: TicketRoutingStageType) =>
    owningOrganizationStage !== null &&
    ticketRoutingStages.findIndex((stage) => stage.type === stageType) >
      owningOrganizationStageIndex;
  const isStageBeforeOwningOrganization = (stageType: TicketRoutingStageType) =>
    owningOrganizationStage !== null &&
    ticketRoutingStages.findIndex((stage) => stage.type === stageType) <
      owningOrganizationStageIndex;

  // Fetch the organization that owns this machine group. Cancellation avoids
  // updating state or showing errors after the group view has unmounted.
  useEffect(() => {
    let cancelled = false;
    setOwningOrganization(null);
    setIsLoadingOwningOrganization(true);

    if (!canViewRelationships) {
      setIsLoadingOwningOrganization(false);
      return () => {
        cancelled = true;
      };
    }

    const loadOwningOrganization = async () => {
      try {
        const result = await getOrganizationByIdUseCase(
          machineGroup.organization_id,
        );
        if (cancelled) return;

        if (result.success && result.data) {
          setOwningOrganization(result.data);
        } else {
          errorSnackbar(result.error || texts.failedToFetchOrganizations);
        }
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          errorSnackbar(texts.failedToFetchOrganizations);
        }
      } finally {
        if (!cancelled) setIsLoadingOwningOrganization(false);
      }
    };

    void loadOwningOrganization();

    return () => {
      cancelled = true;
    };
  }, [canViewRelationships, machineGroup.organization_id]);

  // Rebuild local selections from the saved path whenever the group or its
  // owning type changes. Ignore deleted, malformed, and pre-anchor steps.
  useEffect(() => {
    const steps = machineGroup.routing_path?.steps ?? [];
    const nextActiveStages: TicketRoutingStageType[] = [];
    const addActiveStage = (stage: TicketRoutingStageType) => {
      if (!nextActiveStages.includes(stage)) nextActiveStages.push(stage);
    };
    if (owningOrganizationStage) addActiveStage(owningOrganizationStage);
    const nextSelections = createEmptyRoutingSelections();
    const anchorDropdown = ticketRoutingDropdowns.find(
      (dropdown) => dropdown.type === owningOrganizationStage,
    );
    if (anchorDropdown) {
      nextSelections[anchorDropdown.type] = machineGroup.organization_id;
    }

    steps.forEach((step) => {
      if (!owningOrganizationStage || step.deleted) return;

      if (step.kind === "ai_bot") {
        const botStageIndex = ticketRoutingStages.findIndex(
          (stage) => stage.type === "ai_bot",
        );
        if (botStageIndex > owningOrganizationStageIndex) {
          addActiveStage("ai_bot");
        }
        return;
      }

      if (step.kind !== "organization" || !step.type || step.type === "other") {
        return;
      }

      const stageIndex = ticketRoutingStages.findIndex(
        (stage) => stage.type === step.type,
      );
      if (stageIndex <= owningOrganizationStageIndex) return;

      addActiveStage(step.type);
      if (step.organization_id) {
        nextSelections[step.type] = step.organization_id;
      }
    });

    setActiveRoutingStages(nextActiveStages);
    setRoutingSelections(nextSelections);
    setSavedRoutingStateKey(
      getRoutingStateKey(nextActiveStages, nextSelections),
    );
  }, [
    machineGroup.id,
    machineGroup.organization_id,
    machineGroup.routing_path?.steps,
    owningOrganization?.type,
    owningOrganizationStage,
    owningOrganizationStageIndex,
  ]);

  // An active organization stage must have a selection before the path can be
  // saved; the owning organization's own selection is seeded during hydration.
  const isRoutingPathDirty =
    savedRoutingStateKey !== null &&
    savedRoutingStateKey !==
      getRoutingStateKey(activeRoutingStages, routingSelections);
  const missingOrganizations = ticketRoutingDropdowns
    .filter(
      (dropdown) =>
        activeRoutingStages.includes(dropdown.type) &&
        !routingSelections[dropdown.type],
    )
    .map((dropdown) => texts[dropdown.placeholderKey]);
  // Explain disabled Save states, but leave missing-selection feedback to the
  // warning snackbar shown when the user attempts to save.
  const saveTooltipText =
    !isRoutingPathDirty && missingOrganizations.length === 0
      ? texts.routingPathUnchangedTooltip
      : "";

  // Validate and serialize the current stage selection, then create or update
  // the path and notify the parent so it can refresh the machine-group list.
  const saveRoutingPath = async () => {
    if (
      !canManageRelationships ||
      !owningOrganization ||
      !owningOrganizationStage ||
      isSavingRoutingPath
    )
      return;

    if (missingOrganizations.length > 0) {
      warningSnackbar(
        `${
          texts.selectOrganizationsForActiveRoutingStages
        } ${missingOrganizations.join(", ")}`,
      );
      return;
    }
    if (!isRoutingPathDirty) return;

    // Preserve the configured routing order in the API payload. The machine
    // group's organization remains the owner for its anchored factory stage.
    const steps: UpdateRoutingPathPayload["steps"] = ticketRoutingStages
      .filter((stage) => activeRoutingStages.includes(stage.type))
      .map((stage) => ({
        kind: stage.type === "ai_bot" ? "ai_bot" : "organization",
        ...(stage.type !== "ai_bot" && {
          organization_id:
            stage.type === owningOrganizationStage || stage.type === "factory"
              ? machineGroup.organization_id
              : routingSelections[stage.type],
        }),
      }));
    const pathId = machineGroup.routing_path?.id;
    const payload: UpdateRoutingPathPayload = {
      name: machineGroup.name,
      machine_group_id: machineGroup.id,
      description: machineGroup.routing_path?.description ?? "",
      steps,
    };

    setIsSavingRoutingPath(true);
    try {
      // Existing paths are updated by ID; groups without a path get a new one.
      const result = pathId
        ? await updateRoutingPathUseCase(pathId, payload)
        : await createRoutingPathUseCase(payload);
      if (!result.success) {
        errorSnackbar(
          result.error ||
            (pathId
              ? texts.errorUpdatingRoutingPath
              : texts.errorCreatingRoutingPath),
        );
        return;
      }

      successSnackbar(
        pathId
          ? texts.routingPathUpdatedSuccessfully
          : texts.routingPathCreatedSuccessfully,
      );
      onRoutingPathSaved();
    } finally {
      // Restore the button state after success, failure, or an early return.
      setIsSavingRoutingPath(false);
    }
  };

  // Only stages after the owning organization are editable. Turning a stage
  // off also clears its selected organization so stale values cannot be saved.
  const toggleRoutingStage = (stageType: TicketRoutingStageType) => {
    if (!canManageRelationships || !isStageAfterOwningOrganization(stageType))
      return;

    const isActive = activeRoutingStages.includes(stageType);
    setActiveRoutingStages((stages) =>
      isActive
        ? stages.filter((stage) => stage !== stageType)
        : [...stages, stageType],
    );

    if (isActive && stageType !== "ai_bot") {
      setRoutingSelections((selections) => {
        const nextSelections = { ...selections };
        nextSelections[stageType] = "";
        return nextSelections;
      });
    }
  };

  // Prepare display-only machine names and active routing state for rendering.
  const namedSources = machineGroup.sources.filter(
    (source) => source.name !== null,
  );
  const botIsActive = activeRoutingStages.includes("ai_bot");
  // Attach the current state and accessibility labels to each fixed stage
  // definition so the component only has to render the returned descriptors.
  const routingStages = ticketRoutingStages.map((stage) => {
    const isActive = activeRoutingStages.includes(stage.type);
    const isOwningOrganizationStage = stage.type === owningOrganizationStage;
    const stageLabel = ticketRoutingStageCodes[stage.type];
    const ariaLabel = isOwningOrganizationStage
      ? stage.type === "factory"
        ? texts.factoryRoutingStageAlwaysEnabled
        : `${stageLabel} ${texts.routingStageFixedForOrganization}`
      : `${
          isActive ? texts.deactivateRoutingStage : texts.activateRoutingStage
        } ${stageLabel} ${texts.routingStage}`;

    return {
      ...stage,
      code: ticketRoutingStageCodes[stage.type],
      isActive,
      ariaLabel,
      disabled:
        !canManageRelationships ||
        isLoadingOwningOrganization ||
        !isStageAfterOwningOrganization(stage.type),
    };
  });
  // Build each dropdown's type-matched choices and its current enabled/value
  // state. The factory may include the owning organization as its fixed choice.
  const routingDropdownModels = ticketRoutingDropdowns.map((dropdown) => {
    const placeholder = texts[dropdown.placeholderKey];
    const options = organizations
      .filter(
        (organization) =>
          organization.type === dropdown.type &&
          organization.id !== owningOrganization?.id,
      )
      .map((organization) => ({
        value: organization.id,
        label: organization.name,
      }));
    if (owningOrganizationStage === dropdown.type && owningOrganization) {
      options.unshift({
        value: owningOrganization.id,
        label: owningOrganization.name,
      });
    }

    return {
      type: dropdown.type as RoutingDropdownType,
      id: `routing-${machineGroup.id}-${dropdown.type}`,
      ariaLabel: placeholder,
      disabled:
        !canManageRelationships ||
        isLoadingOwningOrganization ||
        !isStageAfterOwningOrganization(dropdown.type) ||
        !activeRoutingStages.includes(dropdown.type),
      disabledTooltip: isStageBeforeOwningOrganization(dropdown.type)
        ? texts.routingStageBeforeStartTooltip
        : activeRoutingStages.includes(dropdown.type)
        ? undefined
        : texts.routingStageEnableTooltip,
      options,
      placeholder,
      value: routingSelections[dropdown.type] ?? "",
      onChange: (value: string) =>
        setRoutingSelections((previousSelections) => ({
          ...previousSelections,
          [dropdown.type]: value,
        })),
    };
  });

  // Expose only what the view needs: display data, current status, and actions.
  return {
    texts,
    namedSources,
    botIsActive,
    isLoadingOwningOrganization,
    owningOrganizationStage,
    routingStages,
    routingDropdownModels,
    isBotBeforeOwningOrganization: isStageBeforeOwningOrganization("ai_bot"),
    isSaveButtonDisabled:
      isLoadingOwningOrganization ||
      !owningOrganization ||
      isSavingRoutingPath ||
      (!isRoutingPathDirty && missingOrganizations.length === 0),
    saveTooltipText,
    saveRoutingPath,
    toggleRoutingStage,
  };
};
